/*
NOTE: This is very similar to some parts of the CardCoverChooser.

It's designed to be something we can release while CardCoverChooser is still
under development and is intentionally kept separate from the CardCoverChooser
code so we don't have to worry about breaking it, and can easily remove
this once the real CardCoverChooser ships
*/

import React, { useState } from 'react';
import styles from './SimpleCardCoverChooser.less';
import cx from 'classnames';
import { forTemplate } from 'app/common/lib/util/i18n';
import { Icon, IconName } from 'app/common/components/controls';
import { GetFeedback } from 'app/src/components/GetFeedback';
const format = forTemplate('simple_card_cover_chooser');

interface PotentialCover {
  id: string;
  thumbnailUrl: string;
  edgeColor: string;
  isUploading?: boolean;
}

enum SelectionSource {
  remove = 'Remove Button',
  tile = 'Tile',
  upload = 'Upload',
}

interface SimpleCardCoverChooserProps {
  idCoverInitial: string | null;
  potentialCovers: PotentialCover[];
  onChooseCover: (
    attachment: PotentialCover | null,
    numberTried: number,
    source: SelectionSource,
  ) => void;
  onUpload: () => Promise<{ file: File; newCover: Promise<PotentialCover> }>;
}

let nextTemporaryId = 0;

function getFeedback(placement: string, coverCount: number) {
  return (
    <GetFeedback
      surveyName="Simple Card Cover Chooser Feedback"
      className={styles.feedback}
      placement={placement}
      context={String(coverCount)}
    />
  );
}

export const SimpleCardCoverChooser: React.FunctionComponent<
  SimpleCardCoverChooserProps
> = ({ idCoverInitial, potentialCovers, onChooseCover, onUpload }) => {
  const [idCoverSelected, setIdCoverSelected] = useState(idCoverInitial);
  const [, setNumberTried] = useState(0);
  const [covers, setCovers] = useState<(PotentialCover)[]>(potentialCovers);

  const isRemoveDisabled = idCoverSelected === null;

  function chooseCoverAndIncrement(
    cover: PotentialCover | null,
    source: SelectionSource,
  ) {
    setNumberTried((numberTried) => {
      if (cover === null || !cover.isUploading) {
        onChooseCover(cover, numberTried + 1, source);
      }
      setIdCoverSelected(cover && cover.id);
      return numberTried + 1;
    });
  }

  async function upload() {
    try {
      const pendingUpload = await onUpload();

      const placeholder: PotentialCover = {
        id: `placeholder_${nextTemporaryId++}`,
        thumbnailUrl: URL.createObjectURL(pendingUpload.file),
        edgeColor: 'transparent',
        isUploading: true,
      };

      setCovers((prevCovers) => {
        return [...prevCovers, placeholder];
      });
      setIdCoverSelected(placeholder.id);

      let newCover: PotentialCover | undefined = undefined;

      try {
        const uploadedCover = await pendingUpload.newCover;

        // Replace the placeholder with the actual cover
        setCovers((prevCovers) =>
          prevCovers.map(
            (cover) => (cover.id === placeholder.id ? uploadedCover : cover),
          ),
        );

        newCover = uploadedCover;
      } catch (ex) {
        // Upload failed, remove the placeholder
        setCovers((prevCovers) =>
          prevCovers.filter((cover) => cover.id !== placeholder.id),
        );
      }

      setIdCoverSelected((prevIdCoverSelected) => {
        // If the upload worked and they haven't switched to something else,
        // actually choose the uploaded cover
        if (newCover !== undefined && prevIdCoverSelected === placeholder.id) {
          chooseCoverAndIncrement(newCover, SelectionSource.upload);
          return newCover.id;
        } else {
          return prevIdCoverSelected;
        }
      });

      URL.revokeObjectURL(placeholder.thumbnailUrl);
    } catch (ex) {
      // The file was probably too large, we didn't even attempt to upload it
    }
  }

  if (covers.length === 0 && !idCoverSelected) {
    return (
      <>
        <button className={cx('button', styles.upload)} onClick={upload}>
          {format('upload-a-cover-image')}
        </button>
        {getFeedback('Upload Only', 0)}
      </>
    );
  } else {
    return (
      <div>
        <div className={styles.covers}>
          {covers.map((cover) => {
            const { id, edgeColor, thumbnailUrl, isUploading } = cover;

            return (
              <button
                key={id}
                className={cx('button', styles.cover, {
                  [styles.uploading]: isUploading,
                  [styles.selected]: id === idCoverSelected,
                })}
                style={{
                  backgroundColor: edgeColor,
                  backgroundImage: `url('${thumbnailUrl}')`,
                }}
                onClick={() =>
                  chooseCoverAndIncrement(
                    id !== idCoverSelected ? cover : null,
                    SelectionSource.tile,
                  )
                }
              />
            );
          })}
          <button
            className={cx('button', styles.add)}
            onClick={upload}
            aria-label={format('upload-a-cover-image')}
          >
            <Icon name={IconName.Add} />
          </button>
        </div>
        <button
          className={cx('button', styles.remove, {
            disabled: isRemoveDisabled,
          })}
          onClick={() => chooseCoverAndIncrement(null, SelectionSource.remove)}
          disabled={isRemoveDisabled}
        >
          {format('remove-cover')}
        </button>
        {getFeedback('Choose Cover', covers.length)}
      </div>
    );
  }
};
